import React, { useMemo, useState } from "react";
import { Building2, CheckCircle, MapPin, Phone, Sparkles, Star, UserRound } from "lucide-react";
import styles from "./PeopleSearchCard.module.scss";
import {
  PeopleSearchProfile,
  PeopleSearchTypeOption,
} from "../../types/peopleSearch.ts";

type PeopleSearchCardProps = {
  profile: PeopleSearchProfile;
  currentType: PeopleSearchTypeOption;
  onViewProfile?: (profile: PeopleSearchProfile) => void;
  onContactWorker?: (profile: PeopleSearchProfile) => void;
  onOpenWorkerDetails?: (profile: PeopleSearchProfile) => void;
};

const MAX_VISIBLE_TAGS = 1;

const PeopleSearchCard = ({ profile, currentType: _currentType, onViewProfile, onContactWorker, onOpenWorkerDetails }: PeopleSearchCardProps) => {
  const isWorker = profile.type === "WORKER";
  const workerRestricted = isWorker && !profile.hasIdentityValidated;
  const useProfessionalChrome = !isWorker || (isWorker && profile.hasIdentityValidated);
  const [imageError, setImageError] = useState(false);

  const displayName = profile.displayName;

  const tags = useMemo(() => profile.skills.map((skill) => skill.name).filter(Boolean), [profile.skills]);
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const remainingTags = Math.max(0, tags.length - visibleTags.length);

  const initials = useMemo(() => {
    if (profile.raw && (profile.raw as any).type === "COMPANY") {
      return profile.companyName?.charAt(0)?.toUpperCase() ?? "E";
    }
    const words = displayName.trim().split(" ");
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
  }, [displayName, profile.companyName, profile.raw]);

  const typeIcon = isWorker ? <UserRound size={16} /> : (profile.raw as any)?.type === "COMPANY" ? <Building2 size={16} /> : <UserRound size={16} />;
  const typeLabel = isWorker ? "Trabajador" : (profile.raw as any)?.type === "COMPANY" ? "Empresa" : "Profesional";

  const rating = useProfessionalChrome ? (profile.raw as any)?.stars ?? null : null;
  const locationLabel = profile.jurisdictions.map((jur) => jur.name).join(" • ");

  const handlePrimaryAction = () => {
    if (workerRestricted) {
      if (onOpenWorkerDetails) {
        onOpenWorkerDetails(profile);
      } else if (onContactWorker) {
        onContactWorker(profile);
      }
      return;
    }
    if (onViewProfile) {
      onViewProfile(profile);
    }
  };

  const handleSecondaryAction = () => {
    if (useProfessionalChrome && onContactWorker) {
      onContactWorker(profile);
    }
  };

  const showSecondary = useProfessionalChrome && Boolean(onContactWorker);

  const imageSrc = imageError ? null : (profile.raw as any)?.image_profile ?? null;
  const isFeatured =
    (profile.raw as any)?.featured === "ALLOWED" ||
    profile.type === "FEATURED" ||
    profile.source === "FEATURED";

  return (
    <article className={styles.card} data-featured={isFeatured || undefined}>
      <div className={styles.header}>
        <div className={styles.avatar}>
          {imageSrc ? (
            <img src={imageSrc} alt={displayName} onError={() => setImageError(true)} />
          ) : (
            <span className={styles.initials}>{initials}</span>
          )}
        </div>
        <div className={styles.headerInfo}>
          <div className={styles.typeBadge}>
            {typeIcon}
            <span>{typeLabel}</span>
          </div>
          <h3 className={styles.title}>{displayName}</h3>
          {profile.hasIdentityValidated && (
            <span className={styles.verified}>
              <CheckCircle size={14} />
              Verificado
            </span>
          )}
        </div>
        {(isFeatured || useProfessionalChrome) && (
          <div className={styles.headerRight}>
            {isFeatured && (
              <span className={styles.featuredBadge} title="Destacado">
                <Sparkles size={12} />
                Destacado
              </span>
            )}
            {useProfessionalChrome && (
              <div className={styles.rating}>
                <Star className={styles.ratingIcon} size={16} />
                <span className={styles.ratingValue}>{rating ? rating.toFixed(1) : "—"}</span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.meta}>
        {locationLabel && (
          <span className={styles.metaItem}>
            <MapPin size={14} />
            {locationLabel}
          </span>
        )}
        {isWorker && profile.availability && (
          <span className={styles.metaItem}>
            <Phone size={14} />
            {profile.availability}
          </span>
        )}
        {visibleTags.length > 0 && (
          <div className={styles.tags}>
            {visibleTags.map((tag) => (
              <span key={tag} className={styles.tag}>
                {tag}
              </span>
            ))}
            {remainingTags > 0 && <span className={styles.tagExtra}>+{remainingTags}</span>}
          </div>
        )}
      </div>
      <footer className={styles.footer}>
        {showSecondary && (
          <button type="button" className={styles.secondaryButton} onClick={handleSecondaryAction}>
            Contactar
          </button>
        )}
        <button type="button" className={styles.primaryButton} onClick={handlePrimaryAction}>
          {workerRestricted ? "Ver detalles" : "Ver perfil"}
        </button>
      </footer>
    </article>
  );
};

export default PeopleSearchCard;
